import type { FC, ReactNode } from "react"

import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

/** Spread on the designer content behind the notice so it cannot be focused or edited. */
export const inertWhen = (isInert: boolean): { inert?: `` } =>
  isInert ? { inert: `` } : {}

/**
 * Spacing utilities are `!important` because the Adaptive Designer's global reset
 * (`* { padding: 0; margin: 0 }`) is unlayered and would otherwise win.
 *
 * Covers the designer while a Design's saved Bing image cannot be shown, so its layout is not
 * edited against a missing or different background. The existing geometry is left untouched;
 * `children` is the designer's image picker, which only offers supported sources.
 */
export const RetiredImageryUnavailable: FC<{
  children: ReactNode
  onRetry: () => void
  /** The replacement chosen during recovery failed, rather than the saved Bing image. */
  isReplacement: boolean
}> = ({ children, onRetry, isReplacement }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="retired-imagery-unavailable-title"
      aria-describedby="retired-imagery-unavailable-description"
      data-testid="retired-imagery-unavailable"
      className="absolute inset-0 z-[var(--z-modal-underlay)] flex items-center justify-center bg-foreground/40 p-4!"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-elevated text-foreground shadow-popover">
        <div className="flex gap-3 p-5!">
          <ExclamationTriangleIcon
            aria-hidden="true"
            className="size-6 shrink-0 text-accent"
          />
          <div>
            <h2
              id="retired-imagery-unavailable-title"
              className="font-display text-lg font-semibold"
            >
              {t(
                isReplacement
                  ? `retiredImageryReplacementUnavailableTitle`
                  : `retiredImageryUnavailableTitle`,
              )}
            </h2>
            <p
              id="retired-imagery-unavailable-description"
              className="mt-1! text-sm text-muted"
            >
              {t(
                isReplacement
                  ? `retiredImageryReplacementUnavailableDescription`
                  : `retiredImageryUnavailableDescription`,
              )}
            </p>
          </div>
        </div>
        <div className="border-t border-border py-2!">{children}</div>
        <div className="flex justify-between gap-3 border-t border-border px-5! py-3!">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded px-3! py-1.5! text-sm font-medium text-muted hover:bg-glass-highlight focus-visible:outline-2 focus-visible:outline-focus-ring"
          >
            {t(`retiredImageryGoBack`)}
          </button>
          {/* Retrying reloads the designer, which would discard an unsaved replacement */}
          {!isReplacement && (
            <button
              type="button"
              onClick={onRetry}
              className="rounded px-3! py-1.5! text-sm font-medium text-primary hover:bg-primary-soft focus-visible:outline-2 focus-visible:outline-focus-ring"
            >
              {t(`retiredImageryTryAgain`)}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
